import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Download, Camera, Film, Tv, Globe } from 'lucide-react';
import { AnimatedSection, CountUp } from '@/components/AnimatedComponents';

// Client logos/brands
const clients = [
  'NRL',
  'LG',
  'Harvey Norman',
  'Husqvarna',
  'Gardena',
  'Kaboodle',
  'Canon',
  'Home Beautiful',
  'Somfy',
  'Bosch',
  'Bendigo Bank',
  'Qudos Bank'
];

// Services/specializations
const services = [
  { icon: Camera, title: 'Cinematography', description: 'Award-winning camera work for film, TV, and commercials' },
  { icon: Film, title: 'Documentary', description: 'Powerful storytelling that captures the human experience' },
  { icon: Tv, title: 'Commercial', description: 'High-end brand campaigns and content creation' },
  { icon: Globe, title: 'International', description: 'Productions across 45+ countries worldwide' },
];

export default function About() {
  return (
    <main data-testid="about-page">
      {/* Hero */}
      <section className="pt-32 pb-12 md:pt-40 md:pb-16 bg-[#050505]">
        <div className="container-wide">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-[#007AFF] text-sm font-bold uppercase tracking-widest mb-4"
              >
                About
              </motion.p>
              <motion.h1
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tighter mb-6"
              >
                Terry<br />
                <span className="text-[#007AFF]">Webster</span>
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-[#A1A1AA] text-lg md:text-xl leading-relaxed mb-8"
              >
                Sydney-based filmmaker, cinematographer, and producer with 17 years of experience. 
                From award-winning documentaries for ABC to brand campaigns for Australia's leading 
                companies — I tell stories that connect.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="flex flex-wrap gap-4"
              >
                <Link 
                  to="/contact" 
                  className="btn-primary inline-flex items-center gap-2"
                  data-testid="hire-me-about-btn"
                >
                  Hire Me
                  <ArrowRight size={18} />
                </Link>
                <a 
                  href="https://customer-assets.emergentagent.com/job_site-clone-platform/artifacts/oij4vvdf_TerryWebster-CV2026.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary inline-flex items-center gap-2"
                  data-testid="download-cv-btn"
                >
                  <Download size={18} />
                  Download CV
                </a>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="relative"
            >
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_site-clone-platform/artifacts/eyoeieqm_Terry_aboutme_photo_02.jpg"
                  alt="Terry Webster - Top Australian Filmmaker and Cinematographer based in Sydney"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute bottom-4 left-4 bg-[#007AFF] px-5 py-4">
                <p className="text-3xl font-bold">17+</p>
                <p className="text-sm">Years Experience</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 bg-[#0C0C0C] border-y border-[#27272A]">
        <div className="container-wide">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <p className="text-4xl md:text-5xl font-bold text-[#007AFF]">
                <CountUp end={200} suffix="+" />
              </p>
              <p className="text-[#A1A1AA] mt-2">Projects</p>
            </div>
            <div className="text-center">
              <p className="text-4xl md:text-5xl font-bold text-[#007AFF]">
                <CountUp end={15} />
              </p>
              <p className="text-[#A1A1AA] mt-2">Awards</p>
            </div>
            <div className="text-center">
              <p className="text-4xl md:text-5xl font-bold text-[#007AFF]">
                <CountUp end={50} suffix="+" />
              </p>
              <p className="text-[#A1A1AA] mt-2">Brands</p>
            </div>
            <div className="text-center">
              <p className="text-4xl md:text-5xl font-bold text-[#007AFF]">
                <CountUp end={45} />
              </p>
              <p className="text-[#A1A1AA] mt-2">Countries</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services/Skills */}
      <section className="py-20 bg-[#050505]">
        <div className="container-wide">
          <AnimatedSection className="text-center mb-12">
            <p className="text-[#007AFF] text-sm font-bold uppercase tracking-widest mb-4">Expertise</p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tighter">
              What I Do
            </h2>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <AnimatedSection key={service.title} delay={index * 0.1}>
                <div className="p-6 border border-[#27272A] hover:border-[#007AFF] transition-colors h-full">
                  <service.icon className="w-10 h-10 text-[#007AFF] mb-4" />
                  <h3 className="text-xl font-bold mb-2">{service.title}</h3>
                  <p className="text-[#A1A1AA] text-sm">{service.description}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Clients/Brands */}
      <section className="py-16 bg-[#0C0C0C] border-y border-[#27272A]">
        <div className="container-wide">
          <AnimatedSection className="text-center mb-10">
            <p className="text-[#52525B] text-sm uppercase tracking-widest">Trusted by leading brands</p>
          </AnimatedSection>
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12">
            {clients.map((client, index) => (
              <AnimatedSection key={client} delay={index * 0.05}>
                <span className="text-[#52525B] hover:text-white transition-colors text-lg font-medium">
                  {client}
                </span>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Philosophy */}
      <section className="py-20 md:py-28 bg-[#050505]" data-testid="philosophy-section">
        <div className="container-wide">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <AnimatedSection>
              <p className="text-[#007AFF] text-sm font-bold uppercase tracking-widest mb-4">Philosophy</p>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tighter mb-6">
                Crafting Stories<br />
                <span className="text-[#007AFF]">That Matter</span>
              </h2>
              <div className="space-y-5 text-[#A1A1AA] text-lg leading-relaxed">
                <p>
                  Every frame tells a story. In 17 years of filmmaking, I&apos;ve learned that 
                  the most powerful visuals come from understanding the human element behind 
                  every project.
                </p>
                <p>
                  Whether it&apos;s capturing the raw emotion of a documentary subject or 
                  creating the perfect shot for a global brand campaign, I approach each 
                  project with the same dedication to authenticity and craft.
                </p>
                <p>
                  Based in Sydney but working worldwide, I bring Australian creativity and 
                  international perspective to every production.
                </p>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={0.2}>
              <div className="quote-block py-6">
                <p className="text-2xl md:text-3xl text-white italic leading-relaxed">
                  &quot;The camera is just a tool. It&apos;s the story that matters, and 
                  the humanity we bring to telling it.&quot;
                </p>
                <p className="mt-6 text-[#007AFF] font-bold">— Terry Webster</p>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-[#0C0C0C] border-t border-[#27272A]">
        <div className="container-wide text-center">
          <AnimatedSection>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tighter mb-6">
              Let&apos;s Work Together
            </h2>
            <p className="text-[#A1A1AA] text-lg max-w-xl mx-auto mb-8">
              Ready to bring your vision to life? I&apos;m always excited to collaborate 
              on projects that push creative boundaries.
            </p>
            <Link 
              to="/contact" 
              className="btn-primary inline-flex items-center gap-2"
              data-testid="contact-about-cta"
            >
              Get in Touch
              <ArrowRight size={18} />
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </main>
  );
}
